if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.setupMobileLoop();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;
        const activeMedia = this.getRealMediaById(mediaId);
        if (activeMedia) {
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
            element.classList.remove('is-active');
          });
          activeMedia.classList.add('is-active');
        }
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setupMobileLoop() {
        const viewer = this.elements.viewer;
        const slider = viewer?.slider;
        if (this.mql.matches || !slider || viewer.dataset.mobileLoop !== 'true') return;

        const mediaItems = this.getRealMediaItems();
        if (mediaItems.length < 2 || slider.querySelector('[data-loop-clone]')) return;

        const firstClone = mediaItems[0].cloneNode(true);
        const lastClone = mediaItems[mediaItems.length - 1].cloneNode(true);

        this.prepareLoopClone(firstClone, 'after');
        this.prepareLoopClone(lastClone, 'before');

        slider.insertBefore(lastClone, mediaItems[0]);
        slider.appendChild(firstClone);
        viewer.resetPages();

        window.requestAnimationFrame(() => {
          this.jumpToMedia(mediaItems[0]);
        });

        slider.addEventListener('scroll', this.queueMobileLoopNormalization.bind(this), { passive: true });
        slider.addEventListener('touchstart', this.onMobileLoopTouchStart.bind(this), { passive: true });
        slider.addEventListener('touchend', this.onMobileLoopTouchEnd.bind(this), { passive: true });
      }

      prepareLoopClone(clone, position) {
        clone.id = `${clone.id}-loop-${position}`;
        clone.dataset.loopClone = position;
        clone.setAttribute('aria-hidden', 'true');
        clone.classList.remove('is-active');
        clone.querySelectorAll('[id]').forEach((element) => {
          element.id = `${element.id}-loop-${position}`;
        });
      }

      queueMobileLoopNormalization() {
        window.clearTimeout(this.loopNormalizationTimeout);
        this.loopNormalizationTimeout = window.setTimeout(() => {
          this.normalizeMobileLoop();
        }, 120);
      }

      normalizeMobileLoop() {
        const viewer = this.elements.viewer;
        if (this.mql.matches || !viewer?.sliderItemsToShow?.length || !viewer.sliderItemOffset) return;

        const page = Math.round(viewer.slider.scrollLeft / viewer.sliderItemOffset) + 1;
        const currentElement = viewer.sliderItemsToShow[page - 1];
        if (!currentElement?.dataset.loopClone) return;

        const mediaItems = this.getRealMediaItems();
        const targetMedia = currentElement.dataset.loopClone === 'before' ? mediaItems[mediaItems.length - 1] : mediaItems[0];
        this.jumpToMedia(targetMedia);
        this.onSlideChanged({ detail: { currentElement: targetMedia } });
      }

      onMobileLoopTouchStart(event) {
        if (!this.isMobileLoopEnabled()) return;

        this.mobileLoopTouchStartX = event.changedTouches[0].clientX;
      }

      onMobileLoopTouchEnd(event) {
        if (!this.isMobileLoopEnabled() || typeof this.mobileLoopTouchStartX !== 'number') return;

        const swipeDistance = event.changedTouches[0].clientX - this.mobileLoopTouchStartX;
        this.mobileLoopTouchStartX = null;
        if (Math.abs(swipeDistance) < 40) return;

        const mediaItems = this.getRealMediaItems();
        const activeMedia = mediaItems.find((media) => media.classList.contains('is-active'));
        const activeIndex = mediaItems.indexOf(activeMedia);

        if (swipeDistance > 0 && activeIndex === 0) {
          this.jumpToMedia(mediaItems[mediaItems.length - 1]);
          this.onSlideChanged({ detail: { currentElement: mediaItems[mediaItems.length - 1] } });
        } else if (swipeDistance < 0 && activeIndex === mediaItems.length - 1) {
          this.jumpToMedia(mediaItems[0]);
          this.onSlideChanged({ detail: { currentElement: mediaItems[0] } });
        }
      }

      getRealMediaItems() {
        return Array.from(this.elements.viewer.querySelectorAll('[data-media-id]:not([data-loop-clone])'));
      }

      getRealMediaById(mediaId) {
        return this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]:not([data-loop-clone])`);
      }

      isMobileLoopEnabled() {
        return !this.mql.matches && this.elements.viewer?.dataset.mobileLoop === 'true';
      }

      jumpToMedia(media) {
        if (!media) return;
        const slider = this.elements.viewer.slider;
        const formerScrollBehavior = slider.style.scrollBehavior;
        slider.style.scrollBehavior = 'auto';
        slider.scrollTo({ left: media.offsetLeft });
        window.requestAnimationFrame(() => {
          slider.style.scrollBehavior = formerScrollBehavior;
        });
        if (this.elements.viewer.update) {
          this.elements.viewer.update();
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.getRealMediaById(mediaId) ||
          this.elements.viewer.querySelector('[data-media-id]:not([data-loop-clone])');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
